import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { getEnvVar } from '../../config/env';
import { CallingProviderModal } from './components/CallingProviderModal';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

interface CallingProvider {
  id: string;
  name: string;
  country_prefix: string;
  termination_uri: string;
  transport: string;
  sip_auth_username?: string | null;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export const CallingProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<CallingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<'add' | 'edit' | null>(null);
  const [editingProvider, setEditingProvider] = useState<CallingProvider | null>(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/calling-providers`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load calling providers');
      const data = await res.json();
      setProviders(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: {
    name: string;
    country_prefix: string;
    termination_uri: string;
    transport: string;
    sip_auth_username?: string;
    sip_auth_password?: string;
    display_order: number;
    is_active: boolean;
  }) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/calling-providers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.detail || resp.error || 'Failed to create');
    setSuccess('Calling provider added');
    setModalOpen(null);
    loadProviders();
  };

  const handleUpdate = async (
    id: string,
    data: {
      name?: string;
      country_prefix?: string;
      termination_uri?: string;
      transport?: string;
      sip_auth_username?: string;
      sip_auth_password?: string;
      display_order?: number;
      is_active?: boolean;
    }
  ) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/calling-providers/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.detail || resp.error || 'Failed to update');
    setSuccess('Calling provider updated');
    setModalOpen(null);
    setEditingProvider(null);
    loadProviders();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this calling provider? Numbers using this prefix will fall back to defaults.'))
      return;
    try {
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/calling-providers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSuccess('Calling provider removed');
      loadProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Calling Providers</h1>
            <p className="mt-1 text-sm text-gray-600">
              Define SIP termination configs for Retell phone import. Used when assigning numbers to AI employees.
              Indian (+91) and International (+1) have different termination URIs.
            </p>
          </div>
          <button
            onClick={() => {
              setEditingProvider(null);
              setModalOpen('add');
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Add Provider
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 text-green-700 text-sm">{success}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Configured Providers</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {providers.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  No calling providers configured. Add one to define SIP termination for Retell import.
                </div>
              ) : (
                providers.map((p) => (
                  <div
                    key={p.id}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{p.name}</span>
                        <span className="ml-2 text-sm text-gray-500">({p.country_prefix})</span>
                      </div>
                      <span className="text-sm text-gray-600">{p.termination_uri}</span>
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {p.transport}
                      </span>
                      {!p.is_active && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingProvider(p);
                          setModalOpen('edit');
                        }}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1 text-sm"
                      >
                        <Edit2 className="h-4 w-4" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="text-red-600 hover:text-red-900 inline-flex items-center gap-1 text-sm"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>Usage:</strong> When a phone number is assigned to an AI employee, the system matches the
          number&apos;s country prefix (e.g. +91, +1) to a provider here. Default: Indian (+91) → pbx.swsagent.com:5060,
          International (+1) → lk.swsagent.com:5060.
        </div>
      </div>

      {modalOpen === 'add' && (
        <CallingProviderModal
          isAddMode={true}
          existingProvider={null}
          onClose={() => setModalOpen(null)}
          onSave={handleCreate}
        />
      )}
      {modalOpen === 'edit' && editingProvider && (
        <CallingProviderModal
          isAddMode={false}
          existingProvider={editingProvider}
          onClose={() => {
            setModalOpen(null);
            setEditingProvider(null);
          }}
          onSave={(data) => handleUpdate(editingProvider.id, data)}
        />
      )}
    </div>
  );
};
